import { clsx } from "clsx";
import type { ProductVariantDto } from "../../../../api";
import { Text } from "../../../../components/common/typography";

export interface DisplayOption {
  value: string;
  priceLabel?: string;
}

export interface DisplayGroup {
  key: string;
  label: string;
  options: DisplayOption[];
}

export type AxisValues = Record<string, string>;

/**
 * The variant-axis fields the seller can configure as a "choice" in the new
 * product form (see `variant-axis-registry.ts`). Selectors render for these
 * keys whenever the variant has a value set, even if only one variant exists.
 *
 * Other variant-level fields like `model` and `condition` are NOT axes —
 * they go to the static spec section instead.
 */
const STRUCTURED_AXES = [
  { key: "ram", label: "Memory (RAM)" },
  { key: "cpu", label: "CPU" },
  { key: "screenSize", label: "Screen Size" },
  { key: "resolution", label: "Resolution" },
] as const;

export const SELECTOR_AXIS_KEYS: ReadonlySet<string> = new Set([
  ...STRUCTURED_AXES.map((a) => a.key),
  // paren_* keys (Storage / Color from Odoo PTAVs) are also selector axes;
  // they're matched dynamically by prefix in `getVariantAxisValues`.
]);

const COLOR_WORDS = new Set([
  "black", "white", "silver", "gold", "grey", "gray", "blue", "red",
  "green", "titanium", "carbon", "titan", "natural", "space", "rose",
  "graphite", "midnight", "starlight", "platinum", "pink", "purple",
  "navy", "bronze", "copper", "cream", "ivory", "beige", "sand",
]);

function labelForParenAxis(values: string[], index: number): string {
  const lower = values.map((v) => v.toLowerCase());
  if (lower.some((v) => /ssd|hdd|nvme/.test(v))) return "Storage";
  if (lower.some((v) => /\b(win|macos|linux|no os)/.test(v))) return "Operating System";
  if (lower.every((v) => COLOR_WORDS.has(v.split(" ")[0]))) return "Color";
  if (lower.every((v) => /^\d+\s*(gb|tb)\b/.test(v))) return "Capacity";
  return `Option ${index + 1}`;
}

function parenValues(variant: Pick<ProductVariantDto, "displayName" | "name"> | null | undefined): string[] {
  const source = variant?.displayName || variant?.name || "";
  const match = source.match(/\(([^)]+)\)\s*$/);
  if (!match) return [];
  return match[1].split(",").map((s) => s.trim()).filter(Boolean);
}

/**
 * Extract every axis value present on a variant, keyed by a stable axis key.
 * Combines structured DTO fields (ram, cpu, ...) with values encoded in
 * `displayName` parens (Storage/Color from Odoo PTAVs).
 */
export function getVariantAxisValues(variant: ProductVariantDto): AxisValues {
  const out: AxisValues = {};
  for (const axis of STRUCTURED_AXES) {
    const raw = (variant as unknown as Record<string, unknown>)[axis.key];
    if (typeof raw === "string" && raw.trim()) {
      out[axis.key] = raw.trim();
    } else if (raw && typeof raw === "object" && "name" in raw) {
      const name = (raw as { name?: string | null }).name;
      if (name) out[axis.key] = name;
    }
  }
  parenValues(variant).forEach((value, i) => {
    out[`paren_${i}`] = value;
  });
  return out;
}

function isSelectorKey(key: string): boolean {
  return SELECTOR_AXIS_KEYS.has(key) || key.startsWith("paren_");
}

/**
 * Build the variant-selector groups from the variant list. Returns one group
 * per *selector axis* (RAM, CPU, Screen Size, Resolution, plus paren-encoded
 * Storage/Color) whenever any variant has a value set, even if all variants
 * agree on it — the chunky-button selector still renders the single choice.
 *
 * Non-axis variant fields (model, condition, etc.) are intentionally ignored
 * here; they're rendered in the static spec section.
 */
export function buildDisplayGroups(variants: ProductVariantDto[]): DisplayGroup[] {
  if (variants.length === 0) return [];

  const allValues: Record<string, string[]> = {};
  for (const v of variants) {
    const axes = getVariantAxisValues(v);
    for (const [key, value] of Object.entries(axes)) {
      if (!isSelectorKey(key)) continue;
      if (!allValues[key]) allValues[key] = [];
      if (!allValues[key].includes(value)) allValues[key].push(value);
    }
  }

  const labelFor = (key: string, values: string[], orderHint: number): string => {
    const structured = STRUCTURED_AXES.find((a) => a.key === key);
    if (structured) return structured.label;
    if (key.startsWith("paren_")) {
      return labelForParenAxis(values, Number(key.slice("paren_".length)) || orderHint);
    }
    return key;
  };

  const groups: DisplayGroup[] = [];
  let parenOrder = 0;
  for (const [key, values] of Object.entries(allValues)) {
    if (values.length === 0) continue;
    const sorted = [...values].sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
      return a.localeCompare(b);
    });
    groups.push({
      key,
      label: labelFor(key, sorted, parenOrder),
      options: sorted.map((value) => ({ value })),
    });
    if (key.startsWith("paren_")) parenOrder++;
  }
  return groups;
}

/**
 * Annotate group options with relative price labels (`Base Price`, `+QAR 200`,
 * `−QAR 350`) by looking up which variant each option resolves to under the
 * currently-selected combination of other axes.
 */
function getEffectivePrice(v: ProductVariantDto): number {
  const tiers = v.pricelistItems;
  if (Array.isArray(tiers) && tiers.length > 0) {
    const sorted = [...tiers].sort((a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0));
    if (sorted[0].fixedPrice != null) return sorted[0].fixedPrice;
  }
  return (v.listPrice ?? 0) + (v.priceExtra ?? 0);
}

export function withPriceLabels(
  groups: DisplayGroup[],
  variants: ProductVariantDto[],
  current: AxisValues,
): DisplayGroup[] {
  return groups.map((group) => {
    const variantForOption = group.options.map((opt) => {
      const target: AxisValues = { ...current, [group.key]: opt.value };
      return (
        variants.find((v) => {
          const a = getVariantAxisValues(v);
          return Object.entries(target).every(([k, val]) => a[k] === val);
        }) ??
        variants.find((v) => getVariantAxisValues(v)[group.key] === opt.value)
      );
    });
    const prices = variantForOption.map((v) => v ? getEffectivePrice(v) : null);
    const known = prices.filter((p): p is number => p !== null);
    if (known.length === 0) return group;
    const base = Math.min(...known);
    const hasPriceDiffs = known.some((p) => p !== base);
    return {
      ...group,
      options: group.options.map((opt, i) => {
        const price = prices[i];
        if (price === null || !hasPriceDiffs) return opt;
        const delta = price - base;
        const priceLabel = delta > 0
          ? `+QAR ${delta.toLocaleString()}`
          : delta < 0
            ? `-QAR ${Math.abs(delta).toLocaleString()}`
            : "Base Price";
        return { ...opt, priceLabel };
      }),
    };
  });
}

interface ProductAttributeGroupsProps {
  groups: DisplayGroup[];
  current: AxisValues;
  onSelect: (axisKey: string, value: string) => void;
}

export function ProductAttributeGroups({ groups, current, onSelect }: ProductAttributeGroupsProps) {
  if (groups.length === 0) return null;
  return (
    <>
      {groups.map((group) => {
        const hasPriceLabels = group.options.some((o) => o.priceLabel);
        return (
          <div key={group.key} className="flex flex-col gap-2">
            <Text variant="bodyBold" tone="dark">{group.label}</Text>
            <div className="grid grid-cols-3 gap-3">
              {group.options.map((opt) => {
                const isSelected = current[group.key] === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onSelect(group.key, opt.value)}
                    className={clsx(
                      "flex flex-col items-center justify-center gap-0.5 rounded-xl border px-3 transition-colors",
                      hasPriceLabels ? "min-h-[64px] py-2" : "min-h-[44px] py-2",
                      isSelected
                        ? "border-textDark bg-textDark text-white"
                        : "border-borderDefault bg-bgWhite text-textDark hover:border-accentCyan",
                    )}
                  >
                    <Text variant="bodyBold" inline className={isSelected ? "text-white" : "text-textDark"}>
                      {opt.value}
                    </Text>
                    {opt.priceLabel ? (
                      <Text variant="bodyXs" inline className={clsx(
                        isSelected ? "text-white/80" : opt.priceLabel === "Base Price" ? "text-accentGreenDark" : "text-textBody",
                      )}>
                        {opt.priceLabel}
                      </Text>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}
